import { useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Dialog, ICON_SIZE } from '../../design-system';
import './MobileAIPage.css';

/** Drag distance on the header that commits the dismiss instead of springing back. */
const DISMISS_THRESHOLD = 72;

/**
 * The phone form of every AI surface: a full-height page, not a panel and not a
 * sheet.
 *
 * Desktop's panel rests on a promise — the content stays visible while you ask
 * about it — that 393pt cannot keep, so the contract changes rather than
 * degrades. A sheet would say "small, temporary, one decision"; this is a
 * thread you scroll, quote from and come back to, and a half-height sheet
 * would leave four lines of answer above the keyboard.
 *
 * The header names the subject, never the assistant, so "this" is never
 * ambiguous — and it is the same page whether the subject is an article, a
 * list or a deck. Its single exit is the chevron, matching the upward gesture
 * that opened it; dragging the header down does the same thing.
 *
 * Composed on Dialog for focus trap, background inert, Escape and scroll lock,
 * the same way Sheet is — the page owns its geometry, not its a11y plumbing.
 */
const MobileAIPage = ({
  isOpen,
  onClose,
  /** Overline above the subject, e.g. "Asking about". */
  eyebrow,
  /** What the conversation is about: article title, list name, deck name. */
  subject,
  closeLabel,
  className = '',
  children,
}) => {
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartRef = useRef(null);

  const handlePointerDown = (event) => {
    if (!event.isPrimary || event.button !== 0) return;
    // The close button owns its tap; only the rest of the bar starts a drag.
    if (event.target.closest('button')) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = event.clientY;
  };

  const handlePointerMove = (event) => {
    if (dragStartRef.current === null) return;
    setDragOffset(Math.max(0, event.clientY - dragStartRef.current));
  };

  const endDrag = (event) => {
    const startY = dragStartRef.current;
    if (startY === null) return;
    dragStartRef.current = null;
    setDragOffset(0);
    // Cancellation only resets; release coordinates decide even before a render.
    if (event.type === 'pointerup' && event.clientY - startY >= DISMISS_THRESHOLD) {
      onClose?.();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={[eyebrow, subject].filter(Boolean).join(' ')}
      showClose={false}
      animated={false}
      // The page covers the viewport edge to edge, so there is no backdrop to
      // aim at — a stray tap must never be the way out.
      closeOnBackdrop={false}
      overlayClassName="mobile-ai-page__scrim"
      className={`mobile-ai-page ${className}`.trim()}
    >
      <div
        className="mobile-ai-page__surface"
        style={dragOffset ? { transform: `translateY(${dragOffset}px)`, transition: 'none' } : undefined}
      >
        <header
          className="mobile-ai-page__bar"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onLostPointerCapture={endDrag}
        >
          <button
            type="button"
            className="mobile-ai-page__close"
            onClick={onClose}
            aria-label={closeLabel}
            data-testid="mobile-ai-page-close"
          >
            <ChevronDown size={ICON_SIZE.lg} aria-hidden="true" />
          </button>

          <div className="mobile-ai-page__subject">
            {eyebrow && <span className="mobile-ai-page__eyebrow">{eyebrow}</span>}
            <span className="mobile-ai-page__title">{subject}</span>
          </div>
        </header>

        <div className="mobile-ai-page__body">{children}</div>
      </div>
    </Dialog>
  );
};

export default MobileAIPage;
